import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

type Demande = { titre: string; texte?: string; action?: string }

/**
 * Remplace window.confirm par la modale du dashboard.
 * `const { demander, dialogue } = useConfirmation()`, rendre `{dialogue}`,
 * puis `if (!(await demander({ titre: '…' }))) return`.
 */
export function useConfirmation() {
  const { t } = useTranslation()
  const [demande, setDemande] = useState<(Demande & { resoudre: (ok: boolean) => void }) | null>(null)

  const demander = useCallback(
    (d: Demande) => new Promise<boolean>((resoudre) => setDemande({ ...d, resoudre })),
    [],
  )
  const repondre = (ok: boolean) => {
    demande?.resoudre(ok)
    setDemande(null)
  }

  const dialogue = (
    <AlertDialog open={!!demande} onOpenChange={(ouvert) => !ouvert && repondre(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{demande?.titre}</AlertDialogTitle>
          {demande?.texte ? <AlertDialogDescription>{demande.texte}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={() => repondre(true)} className="bg-red-600 hover:bg-red-700 text-white">
            {demande?.action ?? t('common.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { demander, dialogue }
}
